/*
 * Local view-model types for the Pebbl AI feature. Kept independent of the raw API
 * payloads; PebblApiService maps the backend responses into these shapes.
 */

export type Provider = 'openai' | 'gemini';

export interface ProviderOption {
  value: Provider;
  label: string;
}

/** A thinking level a model accepts (`GET /pebbl/models` → `models[].thinking_levels[]`). */
export interface ThinkingLevelOption {
  /** Value sent as `thinking_level` (`low`, `medium`, `high`, `xhigh`, `max`). Case-sensitive. */
  value: string;
  label: string;
  /** Short hint naming the speed / depth trade-off. */
  description?: string;
}

export interface ModelOption {
  value: string;
  label: string;
  /** Short "what it's good for" blurb shown beside the name in the picker. */
  description?: string;
  /** Levels this model accepts, lowest effort first. Empty when the backend lists none. */
  thinkingLevels: ThinkingLevelOption[];
  /** Level used when a turn omits `thinking_level`, or null if unspecified. */
  defaultThinkingLevel: string | null;
}

/** The chat model roster from `GET /pebbl/models` (options + backend default). */
export interface ModelsInfo {
  models: ModelOption[];
  /** The backend's preferred default model id, or null if unspecified. */
  defaultModel: string | null;
}

/* ---- Conversations (sidebar) ---- */

export interface Conversation {
  id: string;
  title: string;
  messageCount: number;
  updatedAt: string;
}

/* ---- Agent activity timeline (reasoning + tool steps) ---- */

export type TimelineKind = 'thinking' | 'tool';

export interface TimelineItem {
  kind: TimelineKind;
  id: string;
  /** For tools: the internal tool name (drives the icon). */
  name?: string;
  /** User-facing label for the step. */
  label: string;
  /** Reasoning text (thinking) or a detail string / SQL (tool). */
  text?: string;
  /** Short outcome summary shown on a finished tool row. */
  summary?: string;
  /** Frozen duration once the step is done. */
  elapsedMs?: number;
  done: boolean;
}

/* ---- Rendered answer components (structured tables / metrics) ---- */

export interface MetricComponent {
  type: 'metric';
  label: string;
  value: string | number;
  unit?: string;
}

export interface TableComponent {
  type: 'table';
  columns: string[];
  rows: (string | number | null)[][];
  totalCount?: number;
}

/**
 * The products a reply pictures. Carries SKUs in display order; name and image are resolved
 * client-side. SKUs are opaque (they hold `/`, spaces, brackets, `+`) and are never shown to
 * the user. `links` is index for index with `skus`: each card's `product_link` — the URL the
 * reply links that product by, which its entry is matched on — or null on a payload sent
 * before `products` existed. `recommended` is index for index too: the product's own
 * `recommended` flag (at most one is true), false on a payload without it.
 */
export interface ProductCardsComponent {
  type: 'product_cards';
  skus: string[];
  links: (string | null)[];
  recommended: boolean[];
}

export type PebblComponent = MetricComponent | TableComponent | ProductCardsComponent;

/* ---- Cost / token usage ---- */

export interface UsageInfo {
  model: string;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  totalTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
  currency: string;
  elapsedSeconds?: number;
}

/* ---- Feedback ---- */

export type FeedbackRating = 'up' | 'down';

/** Reason buckets a user can attach to a thumbs-down (mirrors the backend enum). */
export type FeedbackCategory =
  | 'incorrect_or_incomplete'
  | 'not_what_i_asked_for'
  | 'slow_or_buggy'
  | 'style_or_tone'
  | 'safety_or_legal_concern'
  | 'other';

/** Category options for the thumbs-down feedback dropdown (id sent to the API). */
export const FEEDBACK_CATEGORIES: ReadonlyArray<{ id: FeedbackCategory; name: string }> = [
  { id: 'incorrect_or_incomplete', name: 'Incorrect or incomplete' },
  { id: 'not_what_i_asked_for', name: 'Not what I asked for' },
  { id: 'slow_or_buggy', name: 'Slow or buggy' },
  { id: 'style_or_tone', name: 'Style or tone' },
  { id: 'safety_or_legal_concern', name: 'Safety or legal concern' },
  { id: 'other', name: 'Other' },
];

/* ---- Feedback review (admin section) ---- */

/** Lifecycle status of a submitted feedback item (`GET /pebbl/feedback`). */
export type FeedbackStatus = 'pending' | 'in_review' | 'resolved' | 'rejected';

/**
 * Which chat surface a feedback item came from. The API tags public website chats
 * `'frontend'` and staff chats inside the console `'backend'`; it is null on items
 * filed before the field existed (all of which predate the customer surface).
 */
export type FeedbackAgentType = 'frontend' | 'backend';

/** `agent_type` of a feedback item raised by a customer on the website. */
export const FEEDBACK_AGENT_TYPE_WEBSITE = 'frontend';

/**
 * Who filed a feedback item, resolved server-side. A bare `created_by` is ambiguous —
 * it names a different person in the frontend and backend user tables — so the API
 * reads the right table for the item's `agent_type` and hands us the details.
 */
export interface FeedbackReporter {
  id: number;
  /** Full name, or the email address when both names are blank. */
  name: string;
  email: string;
}

/** A submitted thumbs-down feedback item, shown in the review section. */
export interface FeedbackItem {
  uuid: string;
  /** Category id (usually one of {@link FEEDBACK_CATEGORIES}, but free-text server-side). */
  category: string;
  /** The user's free-text note. */
  message: string;
  status: FeedbackStatus;
  /** Id of the user who submitted the feedback. */
  createdBy: number;
  /** Chat surface it was raised on; null on items filed before the field existed. */
  agentType: FeedbackAgentType | null;
  /** Submitter's details; null when that user no longer exists. */
  reporter: FeedbackReporter | null;
  createdAt: string;
  updatedAt: string;
}

/** One page of feedback items with an opaque cursor for the next (older) page. */
export interface FeedbackPage {
  items: FeedbackItem[];
  nextCursor: string | null;
}

/* ---- Chat attachments (images sent with a user turn) ---- */

/** Hard limits mirrored from the backend (`ATTACHMENT_MAX_*`). The server and the signed
 *  S3 policy are the real gates; these only give a fast, friendly client-side rejection. */
export const ATTACHMENT_MAX_COUNT = 5;
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** SVG is deliberately absent: it renders as markup and is an XSS vector. */
export const ATTACHMENT_TYPES: ReadonlyArray<string> = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

/** `accept` attribute for the composer's file input. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_TYPES.join(',');

/** One image already sent with a message, as returned by the messages API. */
export interface MessageAttachment {
  attachmentUuid: string;
  /** Presigned view URL — time-limited, re-resolvable via `resolveAttachmentUrl`. */
  url: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** Pixel hints, used to reserve an aspect-ratio box so history has no layout shift. */
  width?: number | null;
  height?: number | null;
}

export type PendingAttachmentState = 'uploading' | 'ready' | 'failed';

/**
 * One image the user has attached but not yet sent. `previewUrl` is a local object URL,
 * so a thumbnail appears the instant a file is picked and costs no download.
 */
export interface PendingAttachment {
  localId: string;
  file: File;
  previewUrl: string;
  /** Set once the upload has been authorised; the id sent with the chat turn. */
  attachmentUuid?: string;
  /** Upload progress, 0..1. */
  progress: number;
  state: PendingAttachmentState;
  error?: string;
}

/** Everything the browser needs to upload one image straight to S3. */
export interface AttachmentUploadTicket {
  attachmentUuid: string;
  postUrl: string;
  fields: Record<string, string>;
  expiresIn: number;
}

/** The outcome of validating one uploaded image (`POST /pebbl/attachments/confirm`). */
export interface ConfirmedAttachment {
  attachmentUuid: string;
  status: 'ready' | 'rejected';
  url: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  width?: number | null;
  height?: number | null;
  error?: string | null;
}

/** Whether a picked file is an image type the backend accepts. */
export function isAllowedAttachment(file: File): boolean {
  return ATTACHMENT_TYPES.includes(file.type);
}

/** Human-readable byte size, e.g. "10.0 MB" — used in limits and thumbnail tooltips. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ---- Chat messages ---- */

export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
  role: ChatRole;
  /** Raw markdown for assistant answers; plain text for user turns. */
  text: string;
  timeline: TimelineItem[];
  components: PebblComponent[];
  /** Images sent with a user turn; empty or absent on assistant messages. */
  attachments?: MessageAttachment[];
  usage?: UsageInfo;
  /** Total wall-clock the turn took, in seconds (for "Worked for Ns"). */
  workedSeconds?: number;
  /** Stored assistant-message UUID, once known — the target for thumbs feedback. */
  uuid?: string;
  /** The user's thumbs rating on this assistant message, or null when unrated. */
  feedback?: FeedbackRating | null;
  /** True while this assistant message is the live target of the running stream. */
  streaming?: boolean;
  /** ISO timestamp of the turn: stamped client-side on live turns, and taken from the
   *  message's `created_at` on server-loaded history. */
  createdAt?: string;
  /** Populated to render an error card instead of a bubble. */
  error?: string;
}

/* ---- Chat stream sessions (per-conversation store) ---- */

/**
 * One conversation's live streaming state, owned by ChatStreamStore and keyed by
 * conversation UUID. Holds the transcript, composer draft, and the in-flight turn's
 * AbortController so a stream survives switching away from (and back to) the chat,
 * and so multiple conversations can stream in parallel. ChatViewComponent projects
 * the active session; it no longer owns any of this state itself.
 */
export interface ChatSession {
  /** Conversation UUID — the store map key. */
  id: string;
  /** Full transcript, including the live assistant message while a turn streams. */
  messages: ChatMessage[];
  /** Per-conversation composer draft, preserved across chat switches. */
  input: string;
  /** Live status line ("Thinking…", a tool label) while a turn runs; null when idle. */
  status: string | null;
  /** True while a turn is in flight for THIS conversation (per-thread lock). */
  streaming: boolean;
  /** Abort handle for the in-flight turn; null when idle. */
  controller: AbortController | null;
  /** Sequence cursor for loading older messages, or null when at the start. */
  msgCursor: number | null;
  /** True while the initial history fetch is running (drives the loader). */
  loadingMessages: boolean;
  /** True once history has been fetched at least once (so we don't reload/clobber). */
  loaded: boolean;
  /** Minted by runQuery this session — skip the one reload the activeId change triggers. */
  selfCreated: boolean;
}

/* ---- Pebbl chat stream (SSE) ---- */

/**
 * One parsed SSE chunk from `POST /pebbl/chat`. The `type` discriminates the shape,
 * mirroring the backend's `{"type": ..., "data": {...}}` contract. Trace-only events
 * (reasoning, tool_start, tool_end, api_call, usage) are still received and persisted
 * server-side, but are not rendered in this UI.
 */
export type PebblStreamEvent =
  | { type: 'update'; data: { message: string } }
  | { type: 'reasoning'; data: { text: string } }
  | { type: 'tool_start'; data: { id: string; name: string; label: string; args: Record<string, unknown> } }
  | { type: 'tool_end'; data: { id: string; name?: string; summary: string } }
  | { type: 'api_call'; data: Record<string, unknown> }
  | { type: 'answer'; data: { text: string } }
  | { type: 'component'; data: unknown } // raw — normalised by normalizePebblComponent
  | { type: 'usage'; data: UsageInfo }
  | { type: 'complete'; data?: { message_uuid?: string | null } };

/* ---- Knowledge base ---- */

export type KnowledgeStatus = 'pending' | 'processing' | 'indexed' | 'failed';

/**
 * A peek at one parsed worksheet. A CSV parses to a single sheet with an empty `name`;
 * an Excel workbook yields one entry per worksheet, so each can be indexed separately.
 */
export interface SheetPreview {
  /** Sheet name as it appears in the workbook; empty for a CSV's single sheet. */
  name: string;
  /** Zero-based position in the workbook. */
  index: number;
  /** True when the workbook hides this sheet (listed, but unticked by default). */
  hidden: boolean;
  columns: string[];
  /** Sample rows as stringified cells. */
  rows: string[][];
  totalRows: number;
}

/** Per-sheet column choice sent when indexing a workbook; empty columns means all. */
export interface SheetSelectionInput {
  name: string;
  columns: string[];
}

/** What one sheet contributed to a finished index run. */
export interface IndexedSheet {
  name: string;
  columns: string[];
  chunkCount: number;
}

/** A parsed file's preview — per-sheet tabular data, or Markdown text. */
export interface DocumentPreview {
  /** One entry per parsed sheet, in file order. */
  sheets: SheetPreview[];
  /** The first sheet's headers, kept for the flat (single-sheet) rendering paths. */
  columns: string[];
  rows: string[][];
  totalRows: number;
}

export interface KnowledgeDoc {
  id: string;
  fileName: string;
  status: KnowledgeStatus;
  totalChunks: number;
  totalRows: number;
  indexedColumns: string[];
  /** Per-sheet record of the last index run; empty for CSVs and pre-sheet documents. */
  indexedSheets: IndexedSheet[];
  createdAt: string;
  errorDetail?: string;
  /** Tabular preview (Q&A files). */
  preview?: DocumentPreview;
  /** Markdown body preview (product-info docs). */
  text?: string;
}

/* ---- Website Insights (staff review of logged-in website customers' Pebbl chats) ---- */

/** Date-range KPIs from `GET /pebbl/admin/website-chats/summary`. */
export interface WebsiteChatSummary {
  customers: number;
  accounts: number;
  returningCustomers: number;
  conversations: number;
  questions: number;
  answers: number;
  /** Answers stopped mid-stream whose usage could not be recorded. */
  answersIncompleteUsage: number;
  images: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  thumbsUp: number;
  thumbsDown: number;
  replySecondsSum: number;
  replyCount: number;
  currency: string;
}

export interface WebsiteChatCustomer {
  userId: number | null;
  name: string;
  email: string;
  account: string;
  accountId: number | null;
}

export type WebsiteChatRatingFilter = 'all' | 'helpful' | 'not_helpful';
export type WebsiteChatSort =
  | 'started'
  | 'customer'
  | 'title'
  | 'questions'
  | 'rating'
  | 'tokens'
  | 'cost';
/** By-customer list order; `started` and `title` are the customer's latest chat. */
export type WebsiteCustomerSort =
  | 'started'
  | 'customer'
  | 'title'
  | 'questions'
  | 'rating'
  | 'tokens'
  | 'cost';
export type SortDir = 'asc' | 'desc';

export interface WebsiteChatQuery<S extends string = WebsiteChatSort> {
  from: Date;
  to: Date;
  rating?: WebsiteChatRatingFilter;
  q?: string;
  sort?: S;
  dir?: SortDir;
  page?: number;
  size?: number;
}

/** One row of `GET /pebbl/admin/website-chats`. */
export interface WebsiteChatRow {
  conversationUuid: string;
  title: string;
  lastQuestion: string;
  startedAt: string;
  deletedByCustomer: boolean;
  customer: WebsiteChatCustomer;
  questions: number;
  imageCount: number;
  thumbsUp: number;
  thumbsDown: number;
  totalTokens: number;
  costUsd: number;
}

export interface WebsiteChatPage {
  total: number;
  page: number;
  size: number;
  items: WebsiteChatRow[];
}

/** One customer's matching chats in the period, totalled (`GET /pebbl/admin/website-chats/customers`). */
export interface WebsiteCustomerGroup {
  customer: WebsiteChatCustomer;
  conversations: number;
  firstStartedAt: string;
  lastStartedAt: string;
  questions: number;
  imageCount: number;
  thumbsUp: number;
  thumbsDown: number;
  totalTokens: number;
  costUsd: number;
  /** Their matching chats, newest first. */
  chats: WebsiteChatRow[];
}

export interface WebsiteCustomerPage {
  /** Customers matching the filters - what the pager counts. */
  total: number;
  /** Conversations across those customers. */
  conversations: number;
  page: number;
  size: number;
  items: WebsiteCustomerGroup[];
}

/** One conversation inside a customer's merged thread. */
export interface WebsiteThreadSegment {
  conversation: WebsiteChatRow;
  messages: WebsiteChatMessage[];
}

/**
 * Every matching chat of one customer merged into a single conversation, newest chat first
 * (`GET /pebbl/admin/website-chats/customers/{user_id}`).
 */
export interface WebsiteCustomerThread {
  /** The thread hit the server's message cap; the newest messages are missing. */
  truncated: boolean;
  segments: WebsiteThreadSegment[];
}

export interface WebsiteChatUsage extends UsageInfo {
  /** True when the answer was stopped and only part of the usage is known. */
  partial: boolean;
}

export interface WebsiteChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: string | null;
  attachments: MessageAttachment[];
  /** Stored answer components (product cards) — what puts a picture beside each product. */
  components: PebblComponent[];
  usage: WebsiteChatUsage | null;
  elapsedSeconds: number | null;
  rating: FeedbackRating | null;
}

export interface WebsiteChatTotals {
  questions: number;
  images: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  thumbsUp: number;
  thumbsDown: number;
  avgReplySeconds: number | null;
  models: string[];
}

/** Full transcript from `GET /pebbl/admin/website-chats/{conversation_uuid}`. */
export interface WebsiteChatDetail {
  conversationUuid: string;
  title: string;
  startedAt: string;
  deletedByCustomer: boolean;
  customer: WebsiteChatCustomer;
  totals: WebsiteChatTotals;
  messages: WebsiteChatMessage[];
}
